import React from 'react';

import { Button } from '../ui/button';

import { Label } from '../ui/label';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CheckCircle, Copy, AlertTriangle, Loader2, Mail, Send } from 'lucide-react';

// Sélecteur unifié d'activités (comme dans RegisterPage)

// Auto-complétion SIRET (composant canonique app-wide)

// 🔴 CONFIGURATION CENTRALISÉE - SOURCE UNIQUE

import { useMembreForm } from './useMembreForm';
import MembreFormDialog from './MembreFormDialog';

export default function MembreCreationForm(props) {
  const ctx = useMembreForm(props);
  const { createdCredentials, emailSent, handleCopyCredentials, handleSendCredentialsByEmail, _onOpenChange, _open, sendingEmail, showCredentialsModal, setEmailSent, setShowCredentialsModal } = ctx;
  return (
    <>
      <MembreFormDialog ctx={ctx} />

      {/* Modal Credentials - Affichage des identifiants */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Membre créé avec succès !
            </DialogTitle>
            <DialogDescription>
              Voici les informations de connexion à transmettre au membre.
            </DialogDescription>
          </DialogHeader>

          {createdCredentials && (
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Important</p>
                    <p className="text-sm text-amber-700">
                      Ces informations ne seront affichées qu&apos;une seule fois. 
                      Copiez-les et transmettez-les au membre de manière sécurisée.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm text-gray-600">Email de connexion</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyCredentials(createdCredentials.email)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="font-mono text-lg">{createdCredentials.email}</p>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm text-emerald-800 font-semibold">Lien magique de bienvenue</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyCredentials(createdCredentials.magic_link)}
                      data-testid="created-member-magic-link-copy"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p
                    className="text-xs font-mono text-emerald-900 break-all bg-white border border-emerald-200 rounded-md px-3 py-2 select-all"
                    data-testid="created-member-magic-link"
                  >
                    {createdCredentials.magic_link}
                  </p>
                  <p className="text-[11px] text-emerald-700 mt-2">
                    Valide 7 jours. L&apos;utilisateur sera connecté automatiquement en cliquant et pourra définir son propre mot de passe.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Ce que voit le membre :</strong>
                  </p>
                  <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Reçoit un email <strong>« Bienvenue {createdCredentials.email} »</strong></li>
                    <li>Clique sur <strong>« Activer mon compte en 1 clic »</strong></li>
                    <li>Est connecté automatiquement à son espace</li>
                    <li>Définit son propre mot de passe via une modale</li>
                  </ol>
                  <p className="text-[11px] text-blue-700 mt-2 italic">
                    Aucun mot de passe à transmettre manuellement.
                  </p>
                </div>
              </div>

              {/* Bouton renvoi de l'email magic link */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Renvoyer l&apos;email de bienvenue</p>
                      <p className="text-sm text-green-700">
                        En cas de non réception ou perte du lien
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleSendCredentialsByEmail}
                    disabled={sendingEmail || emailSent}
                    className={emailSent ? "bg-green-600" : "bg-green-500 hover:bg-green-600"}
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : emailSent ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Renvoyé !
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Renvoyer
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              onClick={() => {
                if (createdCredentials) {
                  const text = `Email: ${createdCredentials.email}\nLien magique: ${createdCredentials.magic_link}`;
                  handleCopyCredentials(text);
                }
              }}
              variant="outline"
            >
              <Copy className="h-4 w-4 mr-2" />
              Tout copier
            </Button>
            <Button onClick={() => {
              setShowCredentialsModal(false);
              setEmailSent(false); // Reset pour la prochaine création
            }}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
